export const isValidDate = (value: unknown): value is Date => {
  return value instanceof Date && value.toString() !== 'Invalid Date';
};

export const convertDateToUTC = (date: Date): Date => {
  // for users in timezones behind GMT (e.g. GMT-7:00 for SF), this will create a Date in the future
  // for users in timezones ahead GMT (e.g. GMT+1:00 for London), this will create a Date in the past
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
};

// Given a date, convert the Date to UTC (see above) and then zero the hours relative to UTC
// Returns a new Date in the user's local timezone.
// ex: User in GMT+7:00: input 05/18/2023 at 19:00 (7PM) -> convert to UTC: 05/19/2023 at 02:00 (2AM)
// -> zeroing hours will be 05/19/2023 at 00:00 -> in user's TZ of GMT+7:00: 05/18/2023 at 17:00 (5PM)
export const toUTCMidnight = (d: Date): Date => {
  return new Date(convertDateToUTC(d).setUTCHours(0, 0, 0, 0));
};

// Get midnight of the current day in the user's local timezone
export const getLocalTodayMidnight = (): Date => {
  return new Date(new Date().setHours(0, 0, 0, 0));
};

// Given a string of format YYYY-MM-DD, return a Date object representing the current day at midnight
// in the user's local timezone
export const convertDateStringToLocalDate = (s: string): Date => {
  return new Date(`${s}T00:00`);
};

// Return the month name from the month number (not by index)
export const getMonthNameFromNumber = (n: number): string =>
  [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ][n - 1];

/*
Returns a date range of three months (previous, current, next) based
on `d`.
*/
export const threeMonthPeriod = (
  d: Date,
): { startDate: Date; endDate: Date } => {
  const month = d.getUTCMonth();
  const year = d.getUTCFullYear();

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevMonthYear = month === 0 ? year - 1 : year;

  const nextMonth = month === 11 ? 0 : month + 1;
  const nextMonthYear = month === 11 ? year + 1 : year;

  const followingMonth = nextMonth === 11 ? 0 : nextMonth + 1;
  const followingMonthYear =
    nextMonth === 11 ? nextMonthYear + 1 : nextMonthYear;

  const startDate = new Date(prevMonthYear, prevMonth, 1);
  // NOTE: putting a zero for the date will be result in the last day
  // of the _previous_ month
  const endDate = new Date(followingMonthYear, followingMonth, 0);

  return { startDate, endDate };
};

export const nextMonth = (dateUTC: Date): Date => {
  const month = dateUTC.getUTCMonth();
  const year = dateUTC.getUTCFullYear();
  const day = dateUTC.getUTCDate();
  const hours = dateUTC.getUTCHours();
  const minutes = dateUTC.getUTCMinutes();
  const seconds = dateUTC.getUTCSeconds();

  const nextCalMonth = month === 11 ? 0 : month + 1;
  const nextMonthYear = month === 11 ? year + 1 : year;

  return new Date(
    Date.UTC(nextMonthYear, nextCalMonth, day, hours, minutes, seconds),
  );
};

export const prevMonth = (dateUTC: Date): Date => {
  const month = dateUTC.getUTCMonth();
  const year = dateUTC.getUTCFullYear();
  const day = dateUTC.getUTCDate();
  const hours = dateUTC.getUTCHours();
  const minutes = dateUTC.getUTCMinutes();
  const seconds = dateUTC.getUTCSeconds();

  const prevCalMonth = month === 0 ? 11 : month - 1;
  const prevMonthYear = month === 0 ? year - 1 : year;

  return new Date(
    Date.UTC(prevMonthYear, prevCalMonth, day, hours, minutes, seconds),
  );
};

export const lastDayOfMonth = (dateUTC: Date): Date => {
  const nextMonthDate = nextMonth(dateUTC);
  const year = nextMonthDate.getUTCFullYear();
  const month = nextMonthDate.getUTCMonth();
  const hours = dateUTC.getUTCHours();
  const minutes = dateUTC.getUTCMinutes();
  const seconds = dateUTC.getUTCSeconds();

  return new Date(Date.UTC(year, month, 0, hours, minutes, seconds));
};

export const firstDayOfMonth = (dateUTC: Date): Date => {
  const year = dateUTC.getUTCFullYear();
  const month = dateUTC.getUTCMonth();
  const hours = dateUTC.getUTCHours();
  const minutes = dateUTC.getUTCMinutes();
  const seconds = dateUTC.getUTCSeconds();

  return new Date(Date.UTC(year, month, 1, hours, minutes, seconds));
};

export const withinDateRange = (
  startDate: Date,
  endDate: Date,
  date: Date,
): boolean => {
  const utcStartDate = convertDateToUTC(startDate);
  const utcEndDate = convertDateToUTC(endDate);
  const utcDate = convertDateToUTC(date);

  return utcDate <= utcEndDate && utcDate >= utcStartDate;
};

export const getFormattedDate = (date: Date) => {
  const month = `0${(date.getUTCMonth() + 1).toString()}`.slice(-2);
  const day = `0${date.getUTCDate().toString()}`.slice(-2);
  const year = date.getUTCFullYear().toString();

  return `${month}/${day}/${year}`;
};

export const doesDateMatch = (d1: Date, d2: Date): boolean => {
  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
};

export const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

// Returns a localized date format e.g. January 1, 2021 using UTC
// dates. This prevents things like parsing a string `2020-01-01` and
// displaying `December 31, 2019` because your browser timezone is
// used when parsing into a Date object.
export const formatDateFromString = (
  s: string,
  opts = DEFAULT_DATE_OPTIONS,
): string => {
  let date = new Date(s);
  date = convertDateToUTC(date);
  return date.toLocaleDateString('en-US', opts);
};

type TimeUnit =
  | 'year'
  | 'month'
  | 'week'
  | 'day'
  | 'hour'
  | 'minute'
  | 'second';

export const formatRelativeTimeUTC = (
  date: Date,
  locale: string = 'en',
  dateTimeFormatOptions?: Intl.DateTimeFormatOptions,
) => {
  // Get current time in UTC
  const nowUTC = new Date();
  const nowUTCTime = Date.UTC(
    nowUTC.getUTCFullYear(),
    nowUTC.getUTCMonth(),
    nowUTC.getUTCDate(),
    nowUTC.getUTCHours(),
    nowUTC.getUTCMinutes(),
    nowUTC.getUTCSeconds(),
  );

  // Force input date to be interpreted as UTC
  const dateUTCTime = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

  const diffInSeconds = Math.floor((nowUTCTime - dateUTCTime) / 1000);
  const absDiff = Math.abs(diffInSeconds);

  // If older than 7 days, show human-readable date + local time
  if (absDiff > 7 * 24 * 60 * 60) {
    const dateFormatter = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...dateTimeFormatOptions,
    });
    const timeFormatter = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });

    const formattedDate = dateFormatter.format(date);
    const formattedTime = timeFormatter.format(date);

    return `${formattedDate} at ${formattedTime}`; // e.g., "May 28, 2025 at 12:30 PM EDT"
  }

  // Otherwise, show relative time (e.g., "3 days ago")
  const intervals: Record<TimeUnit, number> = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const [unit, seconds] of Object.entries(intervals) as [
    TimeUnit,
    number,
  ][]) {
    const interval = Math.floor(absDiff / seconds);
    if (interval >= 1) {
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      return rtf.format(-Math.sign(diffInSeconds) * interval, unit);
    }
  }

  return 'just now';
};

export const parseDateStringStrict = (dateStr: string) => {
  const [datePart, timePart] = dateStr.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [time, _ms] = timePart.split('.');
  const [hours, minutes, seconds] = time.split(':').map(Number);

  return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
};
